
import Vue from 'vue'
import { apiService } from '@/service/api.service'
import AdminTableSubheader from '@/components/shared/AdminTableSubheader.vue'

export default Vue.extend({
  name: 'Campaigns',
  components: { AdminTableSubheader },

  data: () => ({
    tab: 0,
    loadingTemplates: false,
    loadingCampaigns: false,
    templates: [] as any[],
    campaigns: [] as any[],
    segments: [] as any[],
    searchTemplate: '',
    searchCampaign: '',

    campaignDialog: { show: false, editing: false, saving: false },
    campaignForm: {
      id: '',
      name: '',
      templateId: '',
      segment: '',
      sendType: 'one_time',
      scheduledAt: '',
      recurringCron: ''
    },

    cronPresets: [
      { label: 'Every hour', value: '0 * * * *' },
      { label: 'Every 6 hours', value: '0 */6 * * *' },
      { label: 'Every day', value: '0 9 * * *' },
      { label: 'Every Monday', value: '0 9 * * 1' },
      { label: 'Every month', value: '0 9 1 * *' },
      { label: 'Every 3 days', value: '0 9 */3 * *' }
    ],

    previewDialog: { show: false, loading: false, template: null as any, html: '' },
    deleteDialog: { show: false, type: '', id: '', name: '' },

    // Recipients dialog
    recipientsDialog: { show: false, campaignId: '', campaignName: '' },
    recipientsLoading: false,
    recipients: [] as any[],
    recipientsMeta: { total: null as number | null, page: 1, perPage: 50, pages: 1 },
    recipientsSearch: '',
    _recipientsSearchTimer: null as any,
    dtPicker: { show: false, tab: 0, date: '', time: '09:00' },
    snack: { show: false, text: '', color: 'success' },

    // Logs tab
    loadingLogs: false,
    logs: [] as any[],
    logsSendTypes: [] as string[],
    logsSearch: '',
    logsFilter: { status: '', sendType: '', dateFrom: '', dateTo: '' },
    logsMeta: { total: 0, page: 1, perPage: 25, pages: 1 },
    logsOptions: { page: 1, itemsPerPage: 25 } as any,
    logsHeaders: [
      { text: 'Recipient', value: 'recipientEmail', sortable: false },
      { text: 'Subject', value: 'subject', sortable: false },
      { text: 'Send Type', value: 'sendType', sortable: false },
      { text: 'Campaign', value: 'campaignName', sortable: false },
      { text: 'Mails', value: 'status', sortable: false, align: 'center' },
      { text: '', value: 'actions', sortable: false, align: 'end' }
    ] as any[],
    _logsSearchTimer: null as any,

    // Mail log details drawer
    logDrawer: { show: false, userId: null as any, userName: '', recipientEmail: '', sendType: '', totalCount: 0 },
    drawerLogs: [] as any[],
    drawerLoading: false
  }),

  computed: {
    templateOptions (): any[] {
      return this.templates.map(t => ({ id: t.id, name: t.name }))
    },
    filteredTemplates (): any[] {
      const q = this.searchTemplate.toLowerCase().trim()
      if (!q) return this.templates
      return this.templates.filter(t =>
        t.name?.toLowerCase().includes(q) || t.subject?.toLowerCase().includes(q)
      )
    },
    filteredCampaigns (): any[] {
      const q = this.searchCampaign.toLowerCase().trim()
      if (!q) return this.campaigns
      return this.campaigns.filter(c =>
        c.name?.toLowerCase().includes(q) || c.targetRules?.segment?.toLowerCase().includes(q)
      )
    },

    cronDescription (): string {
      const cron = (this as any).campaignForm.recurringCron?.trim()
      if (!cron) return ''
      const parts = cron.split(/\s+/)
      if (parts.length !== 5) return ''
      const [min, hour, dom, , dow] = parts
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

      const timeStr = (h: string, m: string) =>
        (h === '*' || m === '*') ? '' : ` at ${h.padStart(2, '0')}:${m.padStart(2, '0')}`

      if (dom === '*' && dow === '*') {
        if (min === '0' && hour === '*') return 'Every hour'
        if (min === '0' && hour.startsWith('*/')) return `Every ${hour.slice(2)} hours`
        return `Every day${timeStr(hour, min)}`
      }
      if (dom === '*') {
        const d = parseInt(dow)
        if (!isNaN(d) && d >= 0 && d <= 6) return `Every ${days[d]}${timeStr(hour, min)}`
      }
      if (dow === '*' && dom.startsWith('*/')) {
        return `Every ${dom.slice(2)} days${timeStr(hour, min)}`
      }
      if (dow === '*' && dom !== '*') {
        return `On the ${dom}. of every month${timeStr(hour, min)}`
      }
      return `Custom: ${cron}`
    }
  },

  watch: {
    logsSearch () {
      clearTimeout(this._logsSearchTimer)
      this._logsSearchTimer = setTimeout(() => {
        this.logsMeta.page = 1
        this.loadLogs()
      }, 350)
    }
  },

  created () {
    this.loadAll()
  },

  methods: {
    async loadAll () {
      await Promise.all([this.loadTemplates(), this.loadCampaigns(), this.loadSegments()])
    },

    async loadTemplates () {
      this.loadingTemplates = true
      try {
        const res: any = await apiService.get('admin-email-templates')
        this.templates = res.data.data || []
      } finally {
        this.loadingTemplates = false
      }
    },

    async loadCampaigns () {
      this.loadingCampaigns = true
      try {
        const res: any = await apiService.get('admin-email-campaigns')
        this.campaigns = res.data.data || []
      } finally {
        this.loadingCampaigns = false
      }
    },

    async loadSegments () {
      try {
        const res: any = await apiService.get('admin-email-campaigns/segments')
        this.segments = res.data.data || []
      } catch {}
    },

    onLogsTab () {
      this.tab = 2
      if (!this.logs.length) this.loadLogs()
      if (!this.logsSendTypes.length) this.loadSendTypes()
    },

    async loadSendTypes () {
      try {
        const res: any = await apiService.get('admin-email-logs/send-types')
        this.logsSendTypes = res.data.data || []
      } catch {}
    },

    onLogsTableOptions (opts: any) {
      const pageChanged = opts.page !== this.logsMeta.page
      const perPageChanged = opts.itemsPerPage !== this.logsMeta.perPage
      if (pageChanged || perPageChanged) {
        this.logsMeta.page = opts.page
        this.logsMeta.perPage = opts.itemsPerPage
        this.loadLogs()
      }
    },

    logsFilterParams (): Record<string, any> {
      const params: Record<string, any> = {}
      if (this.logsSearch) params.search = this.logsSearch
      if (this.logsFilter.status) params.status = this.logsFilter.status
      if (this.logsFilter.sendType) params.send_type = this.logsFilter.sendType
      if (this.logsFilter.dateFrom) params.date_from = this.logsFilter.dateFrom
      if (this.logsFilter.dateTo) params.date_to = this.logsFilter.dateTo
      return params
    },

    async loadLogs () {
      this.loadingLogs = true
      try {
        const params: Record<string, any> = {
          page: this.logsMeta.page,
          per_page: this.logsMeta.perPage,
          group: 1,
          ...this.logsFilterParams()
        }
        const query = Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')
        const res: any = await apiService.get(`admin-email-logs?${query}`)
        this.logs = (res.data.data || []).map((row: any) => ({
          ...row,
          groupKey: `${row.userId}|${row.sendType}|${row.recipientEmail}`
        }))
        this.logsMeta = { ...this.logsMeta, ...res.data.meta }
      } catch {
        this.notify('Failed to load logs', 'error')
      } finally {
        this.loadingLogs = false
      }
    },

    openLogDetails (group: any) {
      this.logDrawer = {
        show: true,
        userId: group.userId,
        userName: group.userName,
        recipientEmail: group.recipientEmail,
        sendType: group.sendType,
        totalCount: group.totalCount
      }
      this.drawerLogs = []
      this.loadDrawerLogs(group)
    },

    async loadDrawerLogs (group: any) {
      this.drawerLoading = true
      try {
        const params: Record<string, any> = {
          per_page: 200,
          user_id: group.userId,
          recipient_email: group.recipientEmail,
          ...this.logsFilterParams()
        }
        if (group.sendType) params.send_type = group.sendType
        const query = Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')
        const res: any = await apiService.get(`admin-email-logs?${query}`)
        this.drawerLogs = res.data.data || []
      } catch {
        this.notify('Failed to load mail details', 'error')
      } finally {
        this.drawerLoading = false
      }
    },

    applyDateTime () {
      if (this.dtPicker.date) {
        const time = this.dtPicker.time || '09:00'
        this.campaignForm.scheduledAt = `${this.dtPicker.date}T${time}:00`
      }
      this.dtPicker.show = false
    },

    openCampaignDialog (campaign?: any) {
      if (campaign) {
        this.campaignForm = {
          id: campaign.id,
          name: campaign.name,
          templateId: campaign.templateId || '',
          segment: campaign.targetRules?.segment || '',
          sendType: campaign.sendType || 'one_time',
          scheduledAt: campaign.scheduledAt || '',
          recurringCron: campaign.recurringCron || ''
        }
        this.campaignDialog.editing = true
      } else {
        this.campaignForm = { id: '', name: '', templateId: '', segment: '', sendType: 'one_time', scheduledAt: '', recurringCron: '' }
        this.campaignDialog.editing = false
      }
      this.campaignDialog.show = true
      if (this.campaignForm.scheduledAt) {
        const [date, timePart] = this.campaignForm.scheduledAt.split('T')
        this.dtPicker.date = date || ''
        this.dtPicker.time = timePart ? timePart.substr(0, 5) : '09:00'
      } else {
        this.dtPicker.date = ''
        this.dtPicker.time = '09:00'
      }
      this.dtPicker.tab = 0
    },

    async saveCampaign () {
      if (!this.campaignForm.name || !this.campaignForm.templateId || !this.campaignForm.segment) {
        this.notify('Name, Template and Audience are required', 'error')
        return
      }
      if (this.campaignForm.sendType === 'recurring' && !this.campaignForm.recurringCron?.trim()) {
        this.notify('Repeat schedule (cron) is required for recurring automations', 'error')
        return
      }
      this.campaignDialog.saving = true
      try {
        const segment = typeof this.campaignForm.segment === 'object'
          ? (this.campaignForm.segment as any)?.key
          : this.campaignForm.segment

        const payload: any = {
          name: this.campaignForm.name,
          templateId: this.campaignForm.templateId,
          targetRules: { segment },
          sendType: this.campaignForm.sendType,
          scheduledAt: this.campaignForm.scheduledAt || null,
          recurringCron: this.campaignForm.sendType === 'recurring' ? (this.campaignForm.recurringCron?.trim() || null) : null,
          status: this.campaignForm.scheduledAt ? 'scheduled' : 'draft'
        }
        if (this.campaignDialog.editing) {
          await apiService.put(`admin-email-campaigns/${this.campaignForm.id}`, payload)
        } else {
          await apiService.post('admin-email-campaigns', payload)
        }
        this.campaignDialog.show = false
        this.notify('Automation saved')
        await this.loadCampaigns()
      } catch (e) {
        this.notify('Save failed', 'error')
      } finally {
        this.campaignDialog.saving = false
      }
    },

    async pauseCampaign (campaign: any) {
      try {
        await apiService.put(`admin-email-campaigns/${campaign.id}`, { status: 'paused' })
        this.notify('Automation paused')
        await this.loadCampaigns()
      } catch { this.notify('Failed to pause', 'error') }
    },

    async resumeCampaign (campaign: any) {
      try {
        await apiService.put(`admin-email-campaigns/${campaign.id}`, { status: 'scheduled' })
        this.notify('Automation resumed')
        await this.loadCampaigns()
      } catch { this.notify('Failed to resume', 'error') }
    },

    async sendNow (campaign: any) {
      try {
        await apiService.post(`admin-email-campaigns/${campaign.id}/send-now`, {})
        this.notify('Automation queued for immediate sending')
        await this.loadCampaigns()
      } catch { this.notify('Failed to send', 'error') }
    },

    async scheduleCampaign (campaign: any) {
      try {
        await apiService.put(`admin-email-campaigns/${campaign.id}`, { status: 'scheduled' })
        this.notify('Automation scheduled')
        await this.loadCampaigns()
      } catch { this.notify('Failed to schedule', 'error') }
    },

    confirmDeleteTemplate (item: any) {
      this.deleteDialog = { show: true, type: 'template', id: item.id, name: item.name }
    },
    confirmDeleteCampaign (item: any) {
      this.deleteDialog = { show: true, type: 'campaign', id: item.id, name: item.name }
    },

    async executeDelete () {
      try {
        if (this.deleteDialog.type === 'template') {
          await apiService.delete(`admin-email-templates/${this.deleteDialog.id}`)
          await this.loadTemplates()
        } else {
          await apiService.delete(`admin-email-campaigns/${this.deleteDialog.id}`)
          await this.loadCampaigns()
        }
        this.notify(`${this.deleteDialog.type === 'campaign' ? 'Automation' : 'Template'} deleted`)
      } catch {
        this.notify('Delete failed', 'error')
      } finally {
        this.deleteDialog.show = false
      }
    },

    openRecipientsDialog (campaign: any) {
      this.recipientsDialog = { show: true, campaignId: campaign.id, campaignName: campaign.name }
      this.recipientsSearch = ''
      this.recipientsMeta = { total: null, page: 1, perPage: 50, pages: 1 }
      this.recipients = []
      this.loadRecipients(1)
    },

    async loadRecipients (page = 1) {
      this.recipientsLoading = true
      try {
        const params: Record<string, any> = { page, per_page: 50 }
        if (this.recipientsSearch) params.search = this.recipientsSearch
        const query = Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')
        const res: any = await apiService.get(
          `admin-email-logs/campaign/${this.recipientsDialog.campaignId}/recipients?${query}`
        )
        this.recipients = res.data.data || []
        this.recipientsMeta = { ...this.recipientsMeta, ...res.data.meta }
      } catch {
        this.notify('Failed to load recipients', 'error')
      } finally {
        this.recipientsLoading = false
      }
    },

    onRecipientsSearch () {
      clearTimeout(this._recipientsSearchTimer)
      this._recipientsSearchTimer = setTimeout(() => this.loadRecipients(1), 350)
    },

    clearRecipientsSearch () {
      this.recipientsSearch = ''
      this.loadRecipients(1)
    },

    closePreview () {
      this.$nextTick(() => { this.previewDialog.show = false })
    },

    async openPreview (template: any) {
      this.previewDialog = { show: true, loading: true, template, html: '' }
      try {
        // Fetch full template (with blocks) if not already present
        let blocks = template.blocks
        if (!blocks || !blocks.length) {
          const res: any = await apiService.get(`admin-email-templates/${template.id}`)
          blocks = res.data.data?.blocks || []
          this.previewDialog.template = { ...template, ...res.data.data }
        }
        this.previewDialog.html = this.buildPreviewHtml(blocks)
      } catch {
        this.notify('Failed to load preview', 'error')
        this.previewDialog.show = false
      } finally {
        this.previewDialog.loading = false
      }
    },

    buildPreviewHtml (blocks: any[]): string {
      const blocksHtml = (blocks || []).map((b: any) => this.renderBlockHtml(b)).join('\n')
      return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{background:#eef2f7;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;padding:32px 0;-webkit-font-smoothing:antialiased}
  .wrap{max-width:600px;margin:0 auto;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 32px rgba(15,23,42,.10),0 1px 4px rgba(15,23,42,.06)}
</style>
</head>
<body><div class="wrap">${blocksHtml}</div></body>
</html>`
    },

    renderBlockHtml (block: any): string {
      const s = (v: unknown, fallback = '') => (v ? String(v) : fallback)
      const esc = (v: unknown) => String(v || '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

      switch (block.type) {
        case 'header':
          return `<div style="background:${s(block.bgColor, '#0f172a')};padding:0">
            <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse"><tr>
              <td style="padding:28px 40px;text-align:center">
                ${block.logoText ? `<div style="display:inline-block;font-size:10px;font-weight:800;letter-spacing:3px;text-transform:uppercase;color:${s(block.textColor, '#fff')};opacity:.55;margin-bottom:18px;padding:5px 12px;border:1px solid rgba(255,255,255,.15);border-radius:4px">${esc(block.logoText)}</div><br>` : ''}
                ${block.title ? `<h1 style="margin:0 0 10px;font-size:28px;font-weight:700;line-height:1.2;color:${s(block.textColor, '#fff')};letter-spacing:-.3px">${esc(block.title)}</h1>` : ''}
                ${block.subtitle ? `<p style="margin:0;font-size:15px;line-height:1.6;color:${s(block.textColor, '#fff')};opacity:.7">${esc(block.subtitle)}</p>` : ''}
              </td>
            </tr></table>
          </div>`

        case 'hero':
          return `<div style="padding:44px 48px;text-align:${s(block.textAlign, 'center')};background:#ffffff">
            ${block.title ? `<h2 style="margin:0 0 12px;font-size:24px;font-weight:700;color:#0f172a;line-height:1.3;letter-spacing:-.2px">${esc(block.title)}</h2>` : ''}
            ${block.subtitle ? `<p style="margin:0;font-size:16px;color:#64748b;line-height:1.65">${esc(block.subtitle)}</p>` : ''}
          </div>`

        case 'text':
          return `<div style="padding:4px 48px 20px;font-size:15px;line-height:1.75;color:#334155;background:#ffffff">${s(block.html)}</div>`

        case 'image': {
          if (!block.url) return '<div style="margin:16px 48px;padding:32px 20px;text-align:center;color:#94a3b8;font-size:13px;border:1.5px dashed #e2e8f0;border-radius:10px;background:#f8fafc">[ Image placeholder ]</div>'
          const img = `<img src="${esc(block.url)}" alt="${esc(block.alt)}" style="max-width:100%;height:auto;border-radius:10px;display:block;margin:0 auto">`
          return `<div style="padding:16px 48px;text-align:center;background:#ffffff">${block.linkUrl ? `<a href="${esc(block.linkUrl)}">${img}</a>` : img}</div>`
        }

        case 'button':
          return `<div style="text-align:center;padding:24px 48px;background:#ffffff">
            <a href="${esc(block.url)}" style="display:inline-block;background:${s(block.bgColor, '#6366f1')};color:${s(block.textColor, '#fff')};padding:15px 40px;border-radius:8px;font-weight:600;font-size:15px;text-decoration:none;letter-spacing:.01em">${esc(block.text)}</a>
          </div>`

        case 'divider':
          return `<div style="padding:4px 48px;background:#ffffff"><hr style="border:none;border-top:1px solid ${s(block.color, '#e2e8f0')}"></div>`

        case 'spacer':
          return `<div style="height:${s(block.height, '24')}px;background:#ffffff"></div>`

        case 'footer':
          return `<div style="background:#f8fafc;padding:28px 48px;text-align:center;border-top:1px solid #e2e8f0">
            ${block.companyName ? `<p style="margin:0 0 8px;font-size:13px;font-weight:700;color:#475569;letter-spacing:.3px;text-transform:uppercase">${esc(block.companyName)}</p>` : ''}
            ${block.supportMail ? `<p style="margin:0 0 12px;font-size:13px;color:#94a3b8">Questions? <a href="mailto:${esc(block.supportMail)}" style="color:#6366f1;text-decoration:none;font-weight:500">${esc(block.supportMail)}</a></p>` : ''}
            <p style="margin:0;font-size:11px;color:#cbd5e1;line-height:1.6">You are receiving this email because you have an account at Cordatus.<br>© ${new Date().getFullYear()} Cordatus. All rights reserved.</p>
          </div>`

        default: return ''
      }
    },

    formatDate (iso: string): string {
      return iso ? new Date(iso).toLocaleString() : ''
    },

    notify (text: string, color = 'success') {
      this.snack = { show: true, text, color }
    }
  }
})
